import { useState } from 'react';
import Download from '~/icons/iconography/download.svg';
import { useExportPriceList, useUserProfile } from '~/services/shop/hooks';
import { useTranslations } from '~/services/umbraco/hooks';
import { Text } from '~/shared/components/Text';
import { useCurrency } from '~/shared/hooks';
import useCanUserAccessFunctionality from '~/shared/utils/mit-fog/canUserAccessFunctionality';
import UserAccountModal from './components/UserAccountModal';
import UserAdminModal from './components/UserAdminModal';
import {
    AccountBottomWrapper,
    AccountTopWrapper,
    AccountWrapper,
    FirstColumn,
    SecondColumn,
    Section,
    StyledLink,
    StyledLinkWithIcon,
    StyledText,
    ThirdColumn,
} from './styled';

export default function AccountInformation() {
    const { translate } = useTranslations();
    const formatCurrency = useCurrency();
    const { data } = useUserProfile();
    const userFunctionality = useCanUserAccessFunctionality();
    const { mutate: sendPriceListRequest } = useExportPriceList();
    const [showOverlay, setShowOverlay] = useState(false);
    const [showUserAdminModal, setShowUserAdminModal] = useState(false);
    const hasAdditionalAccounts = data?.user?.customerIds?.length
        ? data?.user?.customerIds?.length > 1
        : false;

    const onSubmitExportPiceList = () => {
        sendPriceListRequest();
        setShowUserAdminModal(true);
    };

    return (
        <>
            <Section>
                <AccountTopWrapper>
                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.customerNumber')}
                            </Text>
                        </FirstColumn>
                        <SecondColumn>
                            <StyledText>{data?.customer?.id}</StyledText>
                        </SecondColumn>
                        {userFunctionality?.canExportPrice ? (
                            <ThirdColumn>
                                <StyledLinkWithIcon
                                    withIcon
                                    customIcon={<Download />}
                                    onClick={onSubmitExportPiceList}
                                    aria-label={translate('mit-fog.userProfile.exportPrices')}
                                >
                                    {translate('mit-fog.userProfile.exportPrices')}
                                </StyledLinkWithIcon>
                            </ThirdColumn>
                        ) : null}
                    </AccountWrapper>

                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.accountName')}
                            </Text>
                        </FirstColumn>
                        <SecondColumn>
                            <Text>{data?.customer?.name ?? '-'}</Text>
                        </SecondColumn>
                        <ThirdColumn>
                            {hasAdditionalAccounts ? (
                                <StyledLink
                                    aria-label={translate('mit-fog.userProfile.changeAccount')}
                                    as="button"
                                    onClick={() => setShowOverlay(true)}
                                >
                                    {translate('mit-fog.userProfile.changeAccount')}
                                </StyledLink>
                            ) : null}
                        </ThirdColumn>
                    </AccountWrapper>

                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.address')}
                            </Text>
                        </FirstColumn>
                        <SecondColumn>
                            <StyledText>
                                {data?.customer?.addressLine1} {data?.customer?.postalCode}{' '}
                                {data?.customer?.city}
                            </StyledText>
                        </SecondColumn>
                    </AccountWrapper>
                </AccountTopWrapper>

                <AccountBottomWrapper>
                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.creditlimit')}
                            </Text>
                        </FirstColumn>
                        <ThirdColumn>
                            <Text>
                                {formatCurrency(data?.customer?.financialData?.creditLimit ?? 0)}{' '}
                                {translate('mit-fog.valuta.kroner')}
                            </Text>
                        </ThirdColumn>
                    </AccountWrapper>
                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.currentCredit')}
                            </Text>
                        </FirstColumn>
                        <ThirdColumn>
                            <Text>
                                {formatCurrency(data?.customer?.financialData?.currentCredit ?? 0)}{' '}
                                {translate('mit-fog.valuta.kroner')}
                            </Text>
                        </ThirdColumn>
                    </AccountWrapper>
                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.outstandingInvoice')}
                            </Text>
                        </FirstColumn>
                        <ThirdColumn>
                            <Text>
                                {formatCurrency(
                                    data?.customer?.financialData?.outstandingInvoice ?? 0,
                                )}{' '}
                                {translate('mit-fog.valuta.kroner')}
                            </Text>
                        </ThirdColumn>
                    </AccountWrapper>
                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.notInvoiced')}
                            </Text>
                        </FirstColumn>
                        <ThirdColumn>
                            <Text>
                                {formatCurrency(data?.customer?.financialData?.notInvoiced ?? 0)}{' '}
                                {translate('mit-fog.valuta.kroner')}
                            </Text>
                        </ThirdColumn>
                    </AccountWrapper>
                    <AccountWrapper>
                        <FirstColumn>
                            <Text variant="display6">
                                {translate('mit-fog.userProfile.overdueInvoiceAmount')}
                            </Text>
                        </FirstColumn>
                        <ThirdColumn>
                            <Text>
                                {formatCurrency(
                                    data?.customer?.financialData?.overdueInvoiceAmount ?? 0,
                                )}{' '}
                                {translate('mit-fog.valuta.kroner')}
                            </Text>
                        </ThirdColumn>
                    </AccountWrapper>
                </AccountBottomWrapper>
            </Section>

            <UserAdminModal
                showUserAdminModal={showUserAdminModal}
                setShowUserAdminModal={setShowUserAdminModal}
            />

            <UserAccountModal setShowOverlay={setShowOverlay} showOverlay={showOverlay} />
        </>
    );
}
